import styled from 'styled-components';
import ArrowIcon from 'assets/arrow';
import colors from 'styles/colors';
import SortableIcon from 'assets/SortableIcon';
import { fontWeight } from 'styles/common';
import { ComponentProps, ComponentPropsWithRef } from 'react';
import { PropsOf } from 'utils/types.utils';

export const SortingIcon = styled(ArrowIcon)`
  margin-left: 4px;
`;

export const StyledSortableIcon = styled(SortableIcon)`
  margin-left: 2px;
`;

type SortingIconProps = {
  canSort: boolean;
  isSorted: 'asc' | 'desc' | false;
};

export const Sorting = ({ canSort, isSorted }: SortingIconProps) => {
  if (canSort && !isSorted) {
    return <StyledSortableIcon size={16} color={colors.lightGrayIcon} />;
  }
  if (isSorted) {
    return (
      <SortingIcon
        size={14}
        color={colors.fleetDarkGray}
        direction={
          {
            asc: SortingIcon.Direction.Up,
            desc: SortingIcon.Direction.Down,
          }[isSorted]
        }
      />
    );
  }
  return null;
};

export const Header = styled.th`
  & svg {
    flex-shrink: 0;
  }
`;

export const SortableHeader = styled(Header)<{ $sortable?: boolean }>`
  ${(props) => props.$sortable && 'user-select: none; cursor: pointer;'}
`;

export const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 150px;
`;

export const cellPaddingLeftRight = 11;
export const cellPaddingTopBottom = 3;

export const StyledActionButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  display: flex;

  button {
    position: inherit;
    text-transform: uppercase;
  }
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 150px;
`;

export const EmptyStateText = styled.p`
  font-size: 14px;
  font-weight: ${fontWeight.semibold};
  color: ${colors.fleetSlateGray};
`;

export const EmptyState = ({
  placeholder,
  ...props
}: PropsOf<typeof EmptyStateContainer> & { placeholder: string }) => (
  <EmptyStateContainer {...props}>
    <EmptyStateText>{placeholder}</EmptyStateText>
  </EmptyStateContainer>
);

export const TableContainer = styled.div`
  border-spacing: 0;
  border: 1px solid ${colors.selectorBorder};
  border-radius: 4px;
  display: block;
  overflow-y: auto;

  table {
    width: 100%;
    font-size: 12px;
    line-height: 17px;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
  }

  thead th {
    position: sticky;
    top: 0;
    background-color: ${colors.lightBackground};
  }

  th {
    text-align: left;
    font-weight: normal;
    padding: ${cellPaddingTopBottom + 4}px ${cellPaddingLeftRight}px;
    border-right: 1px solid ${colors.selectorBorder};

    & div {
      display: flex;
      align-items: center;
    }
  }

  tr {
    :last-child {
      & td {
        border-bottom: 0;
      }
    }
  }

  td {
    height: 40px;
    padding: ${cellPaddingTopBottom}px ${cellPaddingLeftRight}px;
    border-right: none;
    white-space: nowrap;
  }

  th,
  td {
    :last-child {
      border-right: 0;
    }
    border-bottom: 1px solid ${colors.selectorBorder};
  }

  .action-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    display: flex;

    button {
      position: inherit;
      text-transform: uppercase;
    }
  }
`;
